import { AppRatingBadge } from "@/components/AppRatingBadge";
import { useNavigate } from "react-router-dom";
import { Search, CalendarClock, Navigation, Store } from "lucide-react";
import { CitySearch } from "@/components/CitySearch";
import { PricingCards } from "@/components/PricingCards";
import { BUSINESS_CATEGORIES, HERO_IMG, QUICK_CITIES } from "@/lib/categories";
import { useAuth } from "@/context/AuthContext";
import { InstallButton } from "@/components/InstallApp";

const STEPS = [
  { icon: Search, title: "Type where you are", text: "Any city, state or country on the planet." },
  { icon: CalendarClock, title: "See what's on today", text: "Show times, operating hours, and whether you need a reservation." },
  { icon: Navigation, title: "Tap and go", text: "Call in one tap or open directions in Google Maps, Apple Maps or Waze." },
];

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = (city, pos) => {
    const q = `city=${encodeURIComponent(city)}${pos ? `&near=${pos.lat},${pos.lng}` : ""}`;
    if (user) navigate(`/explore?${q}`);
    else { localStorage.setItem("vv_pending_query", q); navigate("/register"); }
  };

  return (
    <main>
      <section className="relative grain overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19]/40 via-[#0B0F19]/70 to-[#0B0F19]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <p className="rise text-xs font-semibold uppercase tracking-[0.25em] text-[#00E5A3] flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#00E5A3] live-dot" /> Updated daily · worldwide
          </p>
          <h1 className="rise mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl leading-[1.05]" style={{ animationDelay: "80ms" }}>
            Skip asking the locals.<br /><span className="text-[#FF3B6B]">The whole city</span> is in your hand.
          </h1>
          <p className="rise mt-6 text-base md:text-lg text-slate-300 max-w-xl" style={{ animationDelay: "160ms" }}>
            Concerts, comedy, theme parks, dinner cruises, casinos, museums and more — with times, hours, reservations and directions.
          </p>
          <div className="rise mt-10 max-w-2xl" style={{ animationDelay: "240ms" }}>
            <CitySearch onSearch={go} />
            <AppRatingBadge />
            <div className="mt-4 flex flex-wrap gap-2">
              {QUICK_CITIES.map((c) => (
                <button key={c} data-testid={`quick-city-${c.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`} onClick={() => go(c)}
                  className="text-sm px-3.5 py-1.5 rounded-full bg-[#151C2C]/80 border border-[#26334D] hover:border-[#FF3B6B] hover:text-white text-slate-300 transition-colors">
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-base md:text-lg font-semibold text-slate-400 mb-6">Everything worth doing, in one search</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {BUSINESS_CATEGORIES.map((c, i) => (
            <div key={c.id} style={{ animationDelay: `${i * 40}ms` }} className="rise group relative h-32 rounded-2xl overflow-hidden border border-[#26334D]">
              <img src={c.img} alt={c.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/40 to-transparent" />
              <span className="absolute bottom-3 left-3 flex items-center gap-1.5 text-sm font-semibold"><c.icon className="h-4 w-4 text-[#FF3B6B]" />{c.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="home-install-card" className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="rounded-3xl bg-[#151C2C] border border-[#26334D] p-6 sm:p-8 flex flex-wrap items-center gap-6">
          <img src="/icon-192.png" alt="VacaVibe app icon" className="h-20 w-20 rounded-3xl" />
          <div className="flex-1 min-w-[220px]">
            <h2 className="text-xl font-bold">Take VacaVibe with you</h2>
            <p className="text-sm text-slate-400 mt-1">Add it to your phone's home screen — it opens full-screen like a regular app. No App Store needed.</p>
          </div>
          <InstallButton testid="home-install-button" label="Add to home screen" className="h-12 px-6" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-3 gap-8">
        {STEPS.map((s, i) => (
          <div key={s.title} className="flex gap-4">
            <span className="font-mono-vv text-[#FF3B6B] text-sm pt-1">0{i + 1}</span>
            <div>
              <s.icon className="h-6 w-6 text-[#00E5A3] mb-3" />
              <h3 className="text-lg font-semibold">{s.title}</h3>
              <p className="text-slate-400 text-sm mt-1">{s.text}</p>
            </div>
          </div>
        ))}
      </section>

      <section id="pricing" className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Simple pricing</h2>
            <p className="text-base md:text-lg text-slate-400 mt-2">Travelers explore free for 7 days. Businesses get discovered.</p>
          </div>
          <button data-testid="vendor-signup-button" onClick={() => navigate("/register?role=vendor")}
            className="flex items-center gap-2 text-sm text-[#FFB800] hover:text-white transition-colors">
            <Store className="h-4 w-4" /> Own a business? List it here
          </button>
        </div>
        <PricingCards />
      </section>
    </main>
  );
}
